import { TradingMallApiEndpoints } from "../constants/TradingMallApiEndpoints";
import type { IStockListRequest, IFilters } from "../interfaces/StockList.interface";
import { IStockListApiResponse } from "../interfaces/StockListingTable.interface";
import { createDefaultStockFilters } from "../pages/StockListingPage/utils/stockListingHelpers";
import { getStockListData } from "../services/TradingMallService";
import type {
  NormalizedFilterCategory,
  StockFiltersApiResponse,
} from "../pages/StockListingPage/context/StockListingFiltersContext";

const FILTER_QUERY_KEYS: (keyof IFilters)[] = ["index", "sector", "mktcap", "alphabetic"];

function normalizeFilterQueryValue(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw).trim();
  // Avoid stringifying objects into "[object Object]" and polluting query params.
  return "";
}

/**
 * Appends active stock filter selections to a URLSearchParams instance (one entry per value).
 * Use this so filter serialization stays consistent for `/trading/filters` and any GET that mirrors the same shape.
 */
export function appendStockFiltersToSearchParams(
  target: URLSearchParams,
  filters?: IFilters,
): URLSearchParams {
  if (!filters) {
    return target;
  }
  for (const key of FILTER_QUERY_KEYS) {
    const values = filters[key] ?? [];
    for (const raw of values) {
      const s = normalizeFilterQueryValue(raw);
      if (s !== "") {
        target.append(key, s);
      }
    }
  }
  return target;
}

/** Builds the stock filters GET URL including `SERVER_CALL_PARAM` and optional filter query pairs. */
export function buildStockFiltersRequestUrl(filters?: IFilters): string {
  const extra = new URLSearchParams();
  appendStockFiltersToSearchParams(extra, filters);
  const qs = extra.toString();
  return qs ? `${TradingMallApiEndpoints.STOCK_FILTERS}&${qs}` : TradingMallApiEndpoints.STOCK_FILTERS;
}

/**
 * Parameters for fetching stock lists
 */
export interface IFetchStockListParams {
  /** Entity type, e.g. "topgainers", "toplosers", "mostactive" */
  entity: string;
  /** Exchange id (0 = NSE, 2 = BSE) */
  exchId?: number;
  /** Page number */
  page?: number;
  /** Page size */
  limit?: number;
  /** Sort key */
  sort?: string;
  /** Optional API sort column; only included in request when provided */
  sortField?: string;
  /** Filter payload (index, sector, mktcap, alphabetic) */
  filters?: IFilters;
}

/**
 * Generic helper to fetch stock lists (top gainers, top losers, most active, etc).
 *
 * This wraps the TradingMall GET_STOCK_LIST endpoint.
 * 
 * This function is designed for server-side rendering (SSR) and should be called
 * from Next.js server components or API routes.
 *
 * @param params - Request parameters
 * @returns Raw API response data or null on failure
 */
export async function fetchStockList(
  params: IFetchStockListParams,
): Promise<IStockListApiResponse | null> {
  const { entity, exchId = 0, page = 1, limit = 20, sort = "amount_high_to_low", sortField, filters } = params;

  if (!entity) {
    throw new Error("fetchStockList: 'entity' is required");
  }

  const body: IStockListRequest = {
    entity,
    exchId,
    page,
    limit,
    sort,
    ...(sortField ? { sortField } : {}),
    filters: filters || createDefaultStockFilters(),
  };

  try {
    return (await getStockListData(body)) as unknown as IStockListApiResponse | null;
  } catch {
    return null;
  }
}

/**
 * Fetch stock filter categories from the filters API.
 * Designed for server-side rendering – call from Next.js server components.
 *
 * @param filters - When set, sent as query parameters so the service can return server-scoped filter data.
 * @returns Normalized filter categories ready for UI consumption, or empty array on failure.
 */
export async function fetchStockFilters(filters?: IFilters): Promise<NormalizedFilterCategory[]> {
  try {
    const url = buildStockFiltersRequestUrl(filters);
    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
      cache: "no-store",
    });

    if (!res.ok) {
      return [];
    }

    const json = (await res.json()) as StockFiltersApiResponse;
    const categories = Array.isArray(json?.data) ? json.data : [];

    return categories.map((cat) => ({
      key: cat.key,
      name: cat.name,
      options: (cat.values ?? []).map((v) => {
        if (typeof v === "string") {
          return { value: v, label: v };
        }
        return { value: String(v.key), label: v.displayLabel };
      }),
    }));
  } catch {
    return [];
  }
}
